/**
 * NewMenu — global "+ New" creation menu in the header. Lets a user start any
 * top-level object from wherever they are. Reuses the existing creation modals
 * (New Project / Portfolio / Program) and routes to the relevant page for the
 * flows that don't have a modal (Intake request, Log time).
 *
 * Creation entries are gated with useCanManage(); Log Time is personal and
 * always available. Follows the outside-click / aria-haspopup / aria-expanded
 * pattern from UserMenu.
 */

import { useState, useRef, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ChevronDown, FolderKanban, Target, Boxes, Inbox, Clock } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useCanManage } from '@/hooks/useCurrentRole';
import { NewProjectModal } from '@/components/project/NewProjectModal';
import { NewPortfolioModal } from '@/components/portfolio/NewPortfolioModal';
import { NewProgramModal } from '@/components/program/NewProgramModal';

type ModalKind = 'project' | 'portfolio' | 'program';

interface MenuEntry {
  key: string;
  label: string;
  icon: ReactNode;
  onSelect: () => void;
}

export function NewMenu() {
  const navigate = useNavigate();
  const canManage = useCanManage();
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState<ModalKind | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const entries: MenuEntry[] = [
    ...(canManage
      ? ([
          { key: 'project', label: 'New Project', icon: <FolderKanban className="w-4 h-4" />, onSelect: () => setModal('project') },
          { key: 'portfolio', label: 'New Portfolio', icon: <Target className="w-4 h-4" />, onSelect: () => setModal('portfolio') },
          { key: 'program', label: 'New Program', icon: <Boxes className="w-4 h-4" />, onSelect: () => setModal('program') },
          { key: 'intake', label: 'New Intake Request', icon: <Inbox className="w-4 h-4" />, onSelect: () => navigate('/intake?new=1') },
        ] as MenuEntry[])
      : []),
    { key: 'time', label: 'Log Time', icon: <Clock className="w-4 h-4" />, onSelect: () => navigate('/timesheet') },
  ];

  const select = (entry: MenuEntry) => {
    setOpen(false);
    entry.onSelect();
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-1.5 rounded-md bg-brand-navy px-2.5 lg:px-3 py-2 text-sm font-semibold text-white hover:bg-brand-navy/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange"
      >
        <Plus className="w-4 h-4" />
        <span className="hidden sm:inline">New</span>
        <ChevronDown className={cn('w-3 h-3 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 min-w-[200px] bg-white border border-line rounded-md shadow-lg py-1 z-[110] animate-fade-in"
        >
          {entries.map((entry) => (
            <button
              key={entry.key}
              type="button"
              role="menuitem"
              onClick={() => select(entry)}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left text-ink-primary hover:bg-surface-muted transition-colors"
            >
              <span className="text-ink-tertiary">{entry.icon}</span>
              {entry.label}
            </button>
          ))}
        </div>
      )}

      <NewProjectModal isOpen={modal === 'project'} onClose={() => setModal(null)} />
      <NewPortfolioModal isOpen={modal === 'portfolio'} onClose={() => setModal(null)} />
      <NewProgramModal isOpen={modal === 'program'} onClose={() => setModal(null)} />
    </div>
  );
}
